import { ref } from "vue";
import { AppConstants } from "@/constants/AppConstants";
import { AxiosError } from "axios";
import messages from "@/assets/messages.json";
import apiClientAuth from "@/util/apiClientAuth";
interface ParmitionItem {
  categoryId: string;
  categoryName: string;
  systemCategoryName: string;
  categoryUrl: string;
  parmitionFlg: boolean;
}

const items = ref<ParmitionItem[]>(getItemsFromLocalStorage());
const errorMessage = ref<string | null>(null);

function getItemsFromLocalStorage(): ParmitionItem[] {
  const storedItems = localStorage.getItem(AppConstants.PARMITION_SESSION_NAME);
  return storedItems ? JSON.parse(storedItems) : [];
}

function saveItemsToLocalStorage(items: ParmitionItem[]): void {
  localStorage.setItem(
    AppConstants.PARMITION_SESSION_NAME,
    JSON.stringify(items)
  );
}

export function useUserParmitionStore() {
  function addItem(item: ParmitionItem) {
    items.value.push(item);
    saveItemsToLocalStorage(items.value);
  }

  function removeItem(categoryId: string) {
    items.value = items.value.filter((item) => item.categoryId !== categoryId);
    saveItemsToLocalStorage(items.value);
  }

  function updateItem(updatedItem: ParmitionItem) {
    const index = items.value.findIndex(
      (item) => item.categoryId === updatedItem.categoryId
    );
    if (index !== -1) {
      items.value[index] = updatedItem;
      saveItemsToLocalStorage(items.value);
    }
  }

  function getItems(): ParmitionItem[] {
    return items.value;
  }

  /**
   * ユーザー権限取得APIから権限情報を取得し、ストアを更新します。
   * @param {string} agcode -AGコード
   * @param {string} userId -ユーザーID
   * @returns {Promise<string | null>} エラーが発生した場合はエラーメッセージを含む文字列、正常に取得できた場合は null を返す Promise。
   */
  async function fetchParmitionItemsFromAPI(
    agcode: string,
    userId: string
  ): Promise<string | null> {
    try {
      const response = await apiClientAuth.post(
        "/api/Parmition/getUserParmition",
        {
          systemKbn: process.env.VUE_APP_SYSTEM_KBN,
          agCode: agcode,
          userId: userId,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
          withCredentials: true,
        }
      );
      items.value = response.data;
      saveItemsToLocalStorage(items.value);

      // 取得したデータでストアの項目を更新する
      items.value = response.data.userParmition;

      // 取得した項目をローカルストレージに保存する
      saveItemsToLocalStorage(items.value);

      // 前回のエラーメッセージをクリアする
      errorMessage.value = null;

      // エラーが発生しなかったことを示すために null を返す
      return null;
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        switch (axiosError.response.status) {
          case 401:
            errorMessage.value = messages.ERR_USER002;
            break;
          case 500:
            errorMessage.value = messages.ERR_SYS999;
            break;
          default:
            errorMessage.value = messages.ERR_SYS001;
        }
      } else if (axiosError.request) {
        errorMessage.value = messages.ERR_SYS001;
      } else {
        errorMessage.value = messages.ERR_SYS002;
        if (process.env.VUE_APP_DEBUG === "true") {
          console.log("Error: ", axiosError.message);
        }
      }

      return errorMessage.value;
    }
  }

  /**
   * 指定されたシステムカテゴリ名に一致するカテゴリIDを取得します。
   * @param {string} categoryName - システムカテゴリ名
   * @returns {string | null} 一致するカテゴリID。見つからない場合は null。
   */
  function getCategoryIdByName(categoryName: string): string | null {
    const item = items.value.find(
      (item) => item.systemCategoryName === categoryName
    );
    return item ? item.categoryId : null;
  }

  /**
   * 指定されたカテゴリIDに一致するカテゴリ名を取得します。
   * @param {string} categoryId - カテゴリID
   * @returns {string | null} 一致するカテゴリ名。見つからない場合は null。
   */
  function getCategoryNeme(categoryId: string): string | null {
    const item = items.value.find((item) => item.categoryId === categoryId);
    return item ? item.categoryName : null;
  }

  /**
   * 指定されたカテゴリIDに一致するカテゴリURLを取得します。
   * @param {string} categoryId - カテゴリID
   * @returns {string | null} 一致するカテゴリURL。見つからない場合は null。
   */
  function getCategoryURL(categoryId: string): string | null {
    const item = items.value.find((item) => item.categoryId === categoryId);
    return item ? item.categoryUrl : null;
  }

  /**
   * 指定されたカテゴリIDに一致する利用権限を取得します。
   * @param {string} categoryId - カテゴリID
   * @returns {boolean} 利用権限
   */
  function getPermissionFlag(categoryId: string): boolean {
    const item = items.value.find((item) => item.categoryId === categoryId);
    return item ? item.parmitionFlg : false;
  }

  return {
    items,
    addItem,
    removeItem,
    updateItem,
    getItems,
    fetchParmitionItemsFromAPI,
    errorMessage,
    getCategoryIdByName,
    getCategoryNeme,
    getCategoryURL,
    getPermissionFlag,
  };
}
